<template>
  <div class="text-field" :class="{ active: active, 'has-error': !!errorMessage }">
    <div class="text-field-prepend" v-if="$slots.prepend">
      <slot name="prepend" />
    </div>

    <div class="text-field-inner" @click="focusInput">
      <label v-if="label" class="text-field-label" :class="{ visible: active || !!modelValue }">
        {{ label }}
      </label>
      <input
        ref="inputRef"
        class="text-field-input"
        :type="type"
        :name="name"
        :value="modelValue"
        :placeholder="active ? placeholder : label"
        :required="required"
        :autofocus="autoFocus"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keypress.enter="onEnter"
      />

      <div v-if="$slots['append-inner']" class="text-field-append-inner">
        <slot name="append-inner" />
      </div>
    </div>

    <div class="text-field-append" v-if="errorMessage || $slots.append">
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
      <slot name="append" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  modelValue: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'text'
  },
  placeholder: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  autoFocus: {
    type: Boolean,
    default: false
  },
  isValidationAttempt: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'onFocus', 'onBlur', 'onEnter'])

const active = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)
const blurTimeout = ref<number | null>(null)

const errorMessage = computed(() => {
  if (props.required && props.isValidationAttempt && !props.modelValue) {
    return `${props.label} is required.`
  }
  return ''
})

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.value)
}

const onFocus = () => {
  if (blurTimeout.value) {
    clearTimeout(blurTimeout.value)
  }
  active.value = true
  emit('onFocus', true)
}

const onBlur = () => {
  blurTimeout.value = window.setTimeout(() => {
    active.value = false
  }, 200)
  emit('onBlur', true)
}

const onEnter = () => {
  inputRef.value?.blur()
  emit('onEnter', true)
}

const focusInput = () => {
  inputRef.value?.focus()
}

onMounted(() => {
  if (props.autoFocus) {
    inputRef.value?.focus()
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/inc/variables';

.text-field {
  position: relative;
  width: 100%;
  margin-bottom: $spacer-3;
}

.text-field-inner {
  position: relative;
  background: transparent;
  border: 2px solid var(--color-border-primary);
  border-radius: $border-radius;
  height: 48px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  cursor: text;
  transition: $transition-base;

  .text-field.active & {
    border-color: var(--color-border-active);
  }

  .text-field.has-error & {
    border-color: $red50;
  }
}

.text-field-label {
  position: absolute;
  left: $spacer-2;
  top: -$spacer-2;
  background-color: var(--color-bg-primary);
  padding: 0 $spacer-1;
  color: var(--color-text-primary);
  font-family: $font-family-pilat-book;
  font-size: $fs_2;
  font-weight: $font-weight-normal;
  opacity: 0;
  transform: translateY($spacer-1);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  pointer-events: none;
  z-index: 1;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
}

.text-field-input {
  width: 100%;
  border: none;
  background: transparent;
  outline: none;
  color: var(--color-text-primary);
  font-size: $fs_1;
  font-weight: $font-weight-normal;
  font-family: $font-family-base;
  padding: 0;
  line-height: 1.5;

  &::placeholder {
    color: var(--color-text-placeholder);
    -webkit-text-fill-color: var(--color-text-placeholder);
  }

  &:autofill,
  &:autofill:hover,
  &:autofill:focus,
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--color-text-primary);
    -webkit-box-shadow: 0 0 0 1000px var(--color-bg-primary) inset;
    transition: background-color 5000s ease-in-out 0s;
  }
}

.text-field-append-inner {
  margin-left: $spacer-2;
  z-index: 2;
}

.text-field-prepend {
  margin-bottom: $spacer-2;
}

.text-field-append {
  margin-top: $spacer-2;
}

.error-message {
  color: $red50;
  font-size: $fs_3;
  margin-top: $spacer-1;
}
</style>
